import { useNavigate } from "@tanstack/react-router";
import type { OpenCollaborationCreatorsSortColumn, TargetCollaborationCreatorsSortColumn } from "@unbox/api";
import { ChevronDown, Hand, SearchMd, Users01 } from "@untitledui/icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  CollaborationCreatorRowsSource,
  CreatorStatusFilter,
  OpenCollaborationCreator,
  OpenCreatorStatusFilter,
  TargetCollaborationCreator,
} from "@/api/collaborations";
import {
  useCollaborationCreatorRowsFetcher,
  useOpenCollaborationCreators,
  useTargetCollaborationCreators,
  useTargetCollaborationGroupCreators,
} from "@/api/collaborations";
import { usePagination } from "@/api/pagination";
import { GroupedActionMenu } from "@/components/action-menu/grouped-action-menu";
import { SelectAllPagesButton } from "@/components/application/table/select-all-pages-button";
import { UnboxTable } from "@/components/application/table/unbox-table";
import type { UnboxTableColumn } from "@/components/application/table/unbox-table.types";
import { InviteToCollaborationModal } from "@/components/assurance/invite-to-collaboration-modal";
import { Avatar } from "@/components/base/avatar/avatar";
import { Button } from "@/components/base/buttons/button";
import { CloseButton } from "@/components/base/buttons/close-button";
import { Input } from "@/components/base/input/input";
import { FilterPill } from "@/components/filters/filter-pills";
import { MessageCreatorsDialog } from "@/components/message-creator-dialogs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useCrossPageSelection } from "@/hooks/use-cross-page-selection";
import { useDebounce } from "@/hooks/use-debounce";
import { useSelectAllAcrossPages } from "@/hooks/use-select-all-across-pages";
import { useSort } from "@/hooks/use-sort";
import { useTrackedBlockCreators } from "@/hooks/use-tracked-block-creators";
import { buildCreatorActionGroups } from "@/lib/creator-action-groups";
import { BulkCohortModal } from "@/pages/creator-cohorts/bulk-cohort-modal";
import { AddtoListsModal } from "@/pages/creator-lists/add-to-lists-modal";
import { CandidateDetails } from "@/pages/creators/candidate-details";
import { CreatorDetails } from "@/pages/creators/creator-details";
import { MatchQualityCellContent } from "@/pages/explorers/columns/shared-cells";
import { error as toastError, success as toastSuccess } from "@/providers/ToastProvider";
import { compactNumberFormatter } from "@/utils/formatNumber";
import { CREATOR_TAB_EMPTY_STATES, OPEN_ALL_EMPTY_STATE } from "./collaboration-empty-states";
import { type ColumnId, type CreatorStatusTab, getColumnsForTab } from "./collaboration-table-columns";
import { CreatorInvitationStatusBadge } from "./creator-invitation-status-badge";
import { CreatorRowActions } from "./creator-row-actions";
import { CreatorSafetyStatusBadge } from "./creator-safety-status-badge";
import { RelativeDateCell } from "./relative-date-cell";
import { GenderDemoCell, GmvFromVideosCell, LastActionCell, RemovedByCell } from "./removed-creator-cells";
import { SampleProductThumbnails } from "./sample-product-thumbnails";
import { SampleStatusBadge } from "./sample-status-badge";

type Props = {
  collaborationId: string;
  collaborationName: string;
  statusFilter: CreatorStatusTab;
  collaborationType?: "target" | "open";
  groupId?: string;
  isAutoApproved?: boolean;
  /** Active relationship bucket filter from the Creator Breakdown panel. */
  breakdownFilter?: string | null;
  onClearBreakdownFilter?: () => void;
};

const PAGE_SIZE = 20;

const BREAKDOWN_BUCKET_LABELS: Record<string, string> = {
  "3plus": "Worked with 3+ times",
  "1to2": "Worked with 1–2 times",
  first: "First-time invites",
};

const numberFormatter = new Intl.NumberFormat("en-US");

/**
 * Formats a currency amount with proper symbol
 */
const formatCurrency = (amount: number | null | undefined, currency = "USD"): string => {
  if (amount === undefined || amount === null) return "-";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

/**
 * Formats follower count in a compact format (e.g., 1.2K, 3.4M)
 */
const formatFollowers = (count: number | null | undefined): string => {
  if (count === undefined || count === null) return "-";
  if (count >= 1_000_000) {
    return `${(count / 1_000_000).toFixed(1)}M`;
  }
  if (count >= 1_000) {
    return `${(count / 1_000).toFixed(1)}K`;
  }
  return numberFormatter.format(count);
};

/**
 * Render a cell based on column ID
 */
const renderCell = (
  columnId: ColumnId,
  creator: TargetCollaborationCreator | OpenCollaborationCreator,
  collaborationId: string,
  collaborationName: string,
  statusFilter: CreatorStatusTab,
  collaborationType: "target" | "open",
): React.ReactNode => {
  switch (columnId) {
    case "name": {
      const displayName = creator.nickname ?? creator.username ?? creator.tiktokCreatorOpenId;
      const handle = "username" in creator ? creator?.username : null;
      return (
        <div className="flex items-center gap-3">
          <Avatar size="sm" src={creator.avatar ?? undefined} initials={displayName.slice(0, 2).toUpperCase()} />
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-medium text-primary whitespace-nowrap">{displayName}</span>
            {handle && <span className="text-xs text-tertiary whitespace-nowrap">@{handle}</span>}
          </div>
        </div>
      );
    }

    case "status":
      return (
        <CreatorInvitationStatusBadge
          sampleApplicationStatus={creator.sampleApplicationStatus}
          collaborationType={collaborationType}
        />
      );

    case "invitedAt": {
      const date = "invitedAt" in creator ? creator.invitedAt : "joinedAt" in creator ? creator.joinedAt : null;
      return <RelativeDateCell date={date} prefix="" />;
    }

    case "sample":
      return <SampleProductThumbnails products={creator.sampleProducts} maxVisible={3} size="sm" />;

    case "sampleStatus":
      return <SampleStatusBadge sampleApplicationStatus={creator.sampleApplicationStatus} />;

    case "started":
      return <RelativeDateCell date={creator.sampleStartedAt} prefix="~" />;

    case "avgGmv":
      return (
        <span className="text-sm text-primary font-medium">
          {formatCurrency(creator.avgGmvPerBuyerAmount, creator.avgGmvPerBuyerCurrency ?? "USD")}
        </span>
      );

    case "followers":
      return <span className="text-sm text-primary font-medium">{formatFollowers(creator.followerCount)}</span>;

    case "weeklyPosts": {
      const weeklyPosts = "weeklyPosts" in creator ? creator.weeklyPosts : null;
      return (
        <span className="text-sm text-primary font-medium">
          {weeklyPosts != null ? numberFormatter.format(weeklyPosts) : "-"}
        </span>
      );
    }

    case "videoCount":
      return (
        <span className="text-sm text-primary font-medium">
          {creator.videoCount != null ? numberFormatter.format(creator.videoCount) : "-"}
        </span>
      );

    case "unitSales": {
      const totalUnitSales = "totalUnitSales" in creator ? creator.totalUnitSales : null;
      return (
        <span className="text-sm text-primary font-medium">
          {totalUnitSales != null ? numberFormatter.format(totalUnitSales) : "-"}
        </span>
      );
    }

    case "views": {
      const totalViews = "totalViews" in creator ? creator.totalViews : null;
      return <span className="text-sm text-primary font-medium">{formatFollowers(totalViews)}</span>;
    }

    case "safetyStatus": {
      const safetyStatus = "safetyStatus" in creator ? creator.safetyStatus : null;
      return <CreatorSafetyStatusBadge safetyStatus={safetyStatus} />;
    }

    case "videosWithSafety": {
      const safetyStatus = "safetyStatus" in creator ? creator.safetyStatus : null;
      return (
        <div className="flex items-center gap-2">
          <span className="text-sm text-primary font-medium">
            {creator.videoCount != null ? numberFormatter.format(creator.videoCount) : "-"}
          </span>
          {creator.videoCount > 0 && <CreatorSafetyStatusBadge safetyStatus={safetyStatus} />}
        </div>
      );
    }

    case "gmvFromVideos":
      return <GmvFromVideosCell value={"gmvFromVideos" in creator ? creator.gmvFromVideos : null} />;

    case "genderDemo":
      return <GenderDemoCell topGender={"topGender" in creator ? creator.topGender : null} />;

    case "matchQuality":
      return <MatchQualityCellContent score={"matchQualityScore" in creator ? creator.matchQualityScore : null} />;

    case "removedBy":
      return <RemovedByCell removedBy={"removedBy" in creator ? creator.removedBy : null} />;

    case "lastAction":
      return <LastActionCell lastAction={"lastAction" in creator ? creator.lastAction : null} />;

    case "actions":
      return (
        <CreatorRowActions
          creator={creator as TargetCollaborationCreator}
          collaborationId={collaborationId}
          collaborationName={collaborationName}
          tab={statusFilter}
        />
      );

    default:
      return null;
  }
};

/**
 * Table component for displaying creators within a collaboration.
 *
 * Fetches data from the paginated creators endpoint with server-side
 * filtering, sorting, and pagination. Supports both target and open collaborations.
 */
export const CollaborationCreatorsTable = ({
  collaborationId,
  collaborationName,
  statusFilter,
  collaborationType = "target",
  groupId,
  isAutoApproved,
  breakdownFilter,
  onClearBreakdownFilter,
}: Props) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);
  const { sort, setFromDescriptor, descriptor } = useSort<TargetCollaborationCreatorsSortColumn>("nickname");
  const { pagination, page, setPage } = usePagination({ offset: 0, limit: PAGE_SIZE });

  const currentTab = statusFilter;

  type Creator = TargetCollaborationCreator | OpenCollaborationCreator;
  type SelectedCreatorData = {
    tiktokCreatorOpenId: string;
    creatorId: string | null;
    nickname: string | null;
    username: string | null;
    avatar: string | null;
  };
  const {
    selectedKeys,
    selectedData,
    isAllPagesSelected,
    selectedCount,
    clear: clearSelection,
    selectAllAcrossPages,
    createSelectionHandler,
  } = useCrossPageSelection<Creator, SelectedCreatorData>({
    getKey: useCallback((c: Creator) => c.tiktokCreatorOpenId, []),
    toData: useCallback(
      (c: Creator): SelectedCreatorData => ({
        tiktokCreatorOpenId: c.tiktokCreatorOpenId,
        creatorId: c.creatorId ?? null,
        nickname: c.nickname ?? null,
        username: "username" in c ? (c.username ?? null) : null,
        avatar: c.avatar ?? null,
      }),
      [],
    ),
  });

  const [bulkInviteModalOpen, setBulkInviteModalOpen] = useState(false);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [addToListOpen, setAddToListOpen] = useState(false);
  const [addToCohortOpen, setAddToCohortOpen] = useState(false);
  const [selectedCreatorId, setSelectedCreatorId] = useState<string | undefined>();
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | undefined>();
  const blockCreators = useTrackedBlockCreators();
  const selectedCreatorIds = Array.from(selectedData.values())
    .map((c) => c.creatorId)
    .filter((id): id is string => !!id);
  const selectedOpenIds = Array.from(selectedData.keys());

  const handleBulkBlock = async () => {
    try {
      const blocked = await blockCreators.block(selectedOpenIds);
      if (blocked !== null) {
        toastSuccess(`Blocked ${blocked} creator(s). Unblock anytime from Blocked Creators.`);
      }
      clearSelection();
    } catch {
      toastError("Failed to block creators");
    }
  };

  // "Remove from collaborations" isn't in the bulk menu — useBulkRemoveFromCollaborations removes
  // every creator in a selected collaboration, not just the ones selected here, so wiring it up
  // would risk removing creators the user never picked. Needs its own endpoint before it can join
  // this menu.
  const bulkActionGroups = buildCreatorActionGroups({
    onInvite: () => setBulkInviteModalOpen(true),
    onMessage: () => setMessageDialogOpen(true),
    onAddToList: () => setAddToListOpen(true),
    onAddToCohort: () => setAddToCohortOpen(true),
    onBlock: handleBulkBlock,
    onRemove: () => {},
    cohortsEnabled: selectedCreatorIds.length > 0,
    blocklistEnabled: selectedOpenIds.length > 0,
  })
    .map((group) => ({ ...group, items: group.items.filter((item) => item.key !== "remove") }))
    .filter((group) => group.items.length > 0);

  // Reset pagination and selection when status filter, search, or relationship filter changes
  const prevStatusRef = useRef(statusFilter);
  const prevSearchRef = useRef(debouncedSearch);
  const prevRelationshipRef = useRef(breakdownFilter);
  useEffect(() => {
    if (
      prevStatusRef.current !== statusFilter ||
      prevSearchRef.current !== debouncedSearch ||
      prevRelationshipRef.current !== breakdownFilter
    ) {
      setPage(1);
      clearSelection();
      prevStatusRef.current = statusFilter;
      prevSearchRef.current = debouncedSearch;
      prevRelationshipRef.current = breakdownFilter;
    }
  }, [statusFilter, debouncedSearch, breakdownFilter, setPage, clearSelection]);

  const sortOptions = useMemo(
    () => (sort.column ? { column: sort.column, direction: sort.direction } : undefined),
    [sort.column, sort.direction],
  );

  // One sort state drives both collaboration types, but the open route names the
  // invitation column joinedAt and has no view count to sort on.
  const openSortOptions = useMemo(():
    | { column: OpenCollaborationCreatorsSortColumn; direction: "asc" | "desc" }
    | undefined => {
    if (!sortOptions) return undefined;
    const { column, direction } = sortOptions;
    if (column === "invitedAt") return { column: "joinedAt", direction };
    if (column === "totalViews") return undefined;
    return { column, direction };
  }, [sortOptions]);

  // Target collaboration group creators
  const { data: groupData, isLoading: groupIsLoading } = useTargetCollaborationGroupCreators(groupId, {
    status: currentTab as CreatorStatusFilter,
    search: debouncedSearch || undefined,
    pagination,
    sort: sortOptions,
  });

  // Target collaboration creators
  const { data: targetData, isLoading: targetIsLoading } = useTargetCollaborationCreators(
    collaborationType === "target" && !groupId ? collaborationId : undefined,
    {
      status: currentTab as CreatorStatusFilter,
      search: debouncedSearch || undefined,
      pagination,
      sort: sortOptions,
      relationship: (breakdownFilter as "3plus" | "1to2" | "first") || undefined,
    },
  );

  // Open collaboration creators
  const { data: openData, isLoading: openIsLoading } = useOpenCollaborationCreators(
    collaborationType === "open" ? collaborationId : undefined,
    {
      status: currentTab as OpenCreatorStatusFilter,
      search: debouncedSearch || undefined,
      pagination,
      sort: openSortOptions,
    },
  );

  const creators: (TargetCollaborationCreator | OpenCollaborationCreator)[] = groupId
    ? (groupData?.creators ?? [])
    : collaborationType === "target"
      ? (targetData?.creators ?? [])
      : (openData?.creators ?? []);
  const total = groupId
    ? (groupData?.total ?? 0)
    : collaborationType === "target"
      ? (targetData?.total ?? 0)
      : (openData?.total ?? 0);
  const isLoading = groupId ? groupIsLoading : collaborationType === "target" ? targetIsLoading : openIsLoading;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const rowsSource = useMemo((): CollaborationCreatorRowsSource => {
    const search = debouncedSearch || undefined;
    if (groupId) {
      return {
        kind: "group",
        groupId,
        options: { status: currentTab as CreatorStatusFilter, search, sort: sortOptions },
      };
    }
    if (collaborationType === "target") {
      return {
        kind: "target",
        collaborationId,
        options: {
          status: currentTab as CreatorStatusFilter,
          search,
          sort: sortOptions,
          relationship: (breakdownFilter as "3plus" | "1to2" | "first") || undefined,
        },
      };
    }
    return {
      kind: "open",
      collaborationId,
      options: { status: currentTab as OpenCreatorStatusFilter, search, sort: openSortOptions },
    };
  }, [
    groupId,
    collaborationType,
    collaborationId,
    currentTab,
    debouncedSearch,
    sortOptions,
    openSortOptions,
    breakdownFilter,
  ]);

  const fetchChunk = useCollaborationCreatorRowsFetcher(rowsSource);
  const selectAll = useSelectAllAcrossPages({
    fetchChunk,
    total,
    loadedCount: creators.length,
    selection: { selectedCount, isAllPagesSelected, selectAllAcrossPages, clear: clearSelection },
    resetKey: JSON.stringify(rowsSource),
  });

  const handleSelectionChange = useMemo(() => createSelectionHandler(creators), [createSelectionHandler, creators]);

  const genericEmptyState = useMemo(() => {
    if (isAutoApproved && currentTab === "sample_requested") {
      return {
        icon: Hand,
        title: "Sample requests are auto approved",
        description: "Requests are approved as soon as creators request one. To change this, edit the collaboration.",
      };
    }
    const config =
      currentTab === "all" && collaborationType === "open"
        ? OPEN_ALL_EMPTY_STATE
        : CREATOR_TAB_EMPTY_STATES[currentTab];
    const { action } = config;
    const href = action?.href;
    return {
      icon: config.icon,
      title: config.title,
      description: config.description,
      action:
        action && href ? { label: action.label, icon: action.icon, onClick: () => navigate({ to: href }) } : undefined,
    };
  }, [isAutoApproved, currentTab, collaborationType, navigate]);

  const rows = useMemo(() => (isLoading ? undefined : creators), [isLoading, creators]);

  const columns = useMemo<UnboxTableColumn<Creator>[]>(
    () =>
      getColumnsForTab(currentTab).map((col) => ({
        id: col.sortKey ?? col.id,
        label: col.label,
        sortable: col.sortable,
        minWidth: col.minWidth,
        maxWidth: col.maxWidth,
        render: (_value, row) =>
          renderCell(col.id, row, collaborationId, collaborationName, currentTab, collaborationType),
      })),
    [currentTab, collaborationId, collaborationName, collaborationType],
  );

  return (
    <div className="flex flex-col gap-6">
      {/* Header row with counter, search, and filter — or selection bar */}
      {selectedKeys.length > 0 ? (
        <div className="flex flex-row justify-between items-center rounded-lg bg-brand-primary -my-2 p-2 flex-shrink-0">
          <div className="flex flex-row items-center gap-2">
            <CloseButton onClick={selectAll.clear} />
            <span className="text-sm font-medium">{selectedCount.toLocaleString()} Selected</span>
            <SelectAllPagesButton state={selectAll} />
          </div>
          <div className="flex flex-row gap-2 items-center">
            <DropdownMenu>
              <DropdownMenuTrigger asChild disabled={selectAll.isFetching}>
                <Button
                  isDisabled={selectAll.isFetching}
                  iconLeading={<Users01 className="w-4 h-4" />}
                  iconTrailing={<ChevronDown className="w-4 h-4" />}
                >
                  Manage Creator
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={8}>
                <GroupedActionMenu groups={bulkActionGroups} />
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3 flex-shrink-0">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-tertiary">{compactNumberFormatter.format(total)} Creators</span>

            <div className="flex items-center gap-2">
              <Input
                size="sm"
                className="w-64"
                inputClassName="text-sm"
                value={search}
                onChange={setSearch}
                placeholder="Search creators"
                icon={SearchMd}
              />
            </div>
          </div>

          {/* Active breakdown filter chip */}
          {breakdownFilter && BREAKDOWN_BUCKET_LABELS[breakdownFilter] && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-tertiary">Filters:</span>
              <FilterPill
                label="Relationship"
                value={BREAKDOWN_BUCKET_LABELS[breakdownFilter]}
                onRemove={() => onClearBreakdownFilter?.()}
              />
            </div>
          )}
        </div>
      )}

      {/* Table */}
      <UnboxTable<Creator>
        aria-label="Collaboration Creators"
        className="max-h-[60vh]"
        columns={columns}
        rows={rows}
        rowId="tiktokCreatorOpenId"
        onClickRow={(creator) => {
          if (creator.isCreator && creator.creatorId) {
            setSelectedCandidateId(undefined);
            setSelectedCreatorId(creator.creatorId);
          } else if (creator.tiktokCreatorOpenId) {
            setSelectedCreatorId(undefined);
            setSelectedCandidateId(creator.tiktokCreatorOpenId);
          }
        }}
        selection={{ raw: true, selectedKeys: new Set(selectedKeys), onSelectionChange: handleSelectionChange }}
        sorting={{ descriptor, onSortChange: setFromDescriptor }}
        pagination={{ page, totalPages, onPageChange: setPage }}
        hasActiveFilters={!!debouncedSearch}
        emptyState={genericEmptyState}
        filteredEmptyState={{
          onClearFilters: () => {
            setSearch("");
            setPage(1);
          },
        }}
      />

      {/* Bulk action modals */}
      <InviteToCollaborationModal
        creators={Array.from(selectedData.values()).map((c) => ({
          openId: c.tiktokCreatorOpenId,
          username: c.username ?? c.nickname ?? "",
          nickname: c.nickname,
          avatar: c.avatar,
        }))}
        open={bulkInviteModalOpen}
        onOpenChange={setBulkInviteModalOpen}
        onSuccess={clearSelection}
        excludeCollaborationId={collaborationId}
      />
      <MessageCreatorsDialog
        recipients={Array.from(selectedData.values()).map((c) => ({
          creatorId: c.tiktokCreatorOpenId,
          handle: c.nickname ?? c.username ?? null,
          thumbnail: c.avatar,
        }))}
        open={messageDialogOpen}
        onOpenChange={setMessageDialogOpen}
        onSuccess={clearSelection}
      />
      <AddtoListsModal
        selectedCreators={selectedCreatorIds}
        onAddToList={() => {
          setAddToListOpen(false);
          clearSelection();
        }}
        open={addToListOpen}
        onOpenChange={setAddToListOpen}
      />
      <BulkCohortModal
        creatorIds={selectedCreatorIds}
        open={addToCohortOpen}
        onOpenChange={setAddToCohortOpen}
        onApplied={() => {
          setAddToCohortOpen(false);
          clearSelection();
        }}
      />

      {/* Brand creator details drawer */}
      <CreatorDetails
        id={selectedCreatorId}
        onClose={() => {
          setSelectedCreatorId(undefined);
          setSelectedCandidateId(undefined);
        }}
      />

      {/* Candidate details drawer */}
      <CandidateDetails
        id={selectedCandidateId}
        onClose={() => {
          setSelectedCandidateId(undefined);
          setSelectedCreatorId(undefined);
        }}
      />
    </div>
  );
};
